import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { alpha, Box, IconButton, useTheme } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_UP_RATIO = 1.2;
const SCALE_DOWN_RATIO = 1 / SCALE_UP_RATIO;

const formatScale = (scale: number) => `${Math.round(scale * 100)}%`;

interface ZoomControlsProps {
  scale: number;
  onScaleChange: (scale: number) => void;
}

/**
 * Floating zoom controls for PDF preview.
 * Provides +/- buttons and an editable percentage input.
 */
export const ZoomControls = ({ scale, onScaleChange }: ZoomControlsProps) => {
  const theme = useTheme();
  const [pendingScale, setPendingScale] = useState(formatScale(scale));

  // Clamp scale to valid range
  const clampScale = useCallback(
    (newScale: number) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale)),
    [],
  );

  const handleZoomIn = useCallback(() => {
    onScaleChange(clampScale(scale * SCALE_UP_RATIO));
  }, [scale, onScaleChange, clampScale]);

  const handleZoomOut = useCallback(() => {
    onScaleChange(clampScale(scale * SCALE_DOWN_RATIO));
  }, [scale, onScaleChange, clampScale]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPendingScale(event.target.value);
  }, []);

  const handleInputBlur = useCallback(() => {
    const parsed = parseFloat(pendingScale.replace(/%/g, '')) / 100;
    if (!Number.isNaN(parsed)) {
      onScaleChange(clampScale(parsed));
    } else {
      // Reset to current scale if invalid
      setPendingScale(formatScale(scale));
    }
  }, [pendingScale, onScaleChange, clampScale, scale]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleInputBlur();
      }
    },
    [handleInputBlur],
  );

  // Sync pending scale with actual scale
  useEffect(() => {
    setPendingScale(formatScale(scale));
  }, [scale]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.common.black, 0.75),
        color: theme.palette.common.white,
        pointerEvents: 'auto',
      }}
    >
      <IconButton
        size="small"
        color="inherit"
        onClick={handleZoomOut}
        disabled={scale <= MIN_SCALE}
        aria-label="Zoom out"
      >
        <RemoveIcon fontSize="small" />
      </IconButton>

      <input
        type="text"
        value={pendingScale}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        aria-label="Zoom level"
        style={{
          width: '60px',
          padding: theme.spacing(0.5, 1),
          margin: 0,
          border: 0,
          borderRadius: theme.shape.borderRadius,
          textAlign: 'center',
          background: alpha(theme.palette.common.white, 0.15),
          color: 'inherit',
          fontSize: theme.typography.body2.fontSize,
        }}
      />

      <IconButton
        size="small"
        color="inherit"
        onClick={handleZoomIn}
        disabled={scale >= MAX_SCALE}
        aria-label="Zoom in"
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
